import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { toast } from "sonner";
import {
  Mic, Square, Loader2, Trash2, Sparkles, BookOpen,
  ChevronDown, Lock, Wand2, Check, X,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
};
const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function NoteCard({ note, onDelete, onAction, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`voice-note-${note.id}`} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg text-white leading-snug">{note.title}</h3>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-slate-400">{fmtDate(note.created_at)}</span>
            {note.used && (
              <span data-testid={`voice-note-used-${note.id}`} className="inline-flex items-center gap-1 text-[10px] font-medium text-green-300 bg-green-500/10 border border-green-500/30 rounded-full px-2 py-0.5">
                <Check className="w-3 h-3" /> Explored
              </span>
            )}
          </div>
        </div>
        <button data-testid={`voice-note-delete-${note.id}`} onClick={() => onDelete(note)}
                className="shrink-0 text-slate-500 hover:text-red-500 transition-colors" aria-label="Delete note">
          <Trash2 className="w-4 h-4" strokeWidth={1.6} />
        </button>
      </div>

      {note.summary && <p className="text-sm text-slate-400 leading-relaxed">{note.summary}</p>}

      {note.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {note.tags.map((t, i) => (
            <span key={i} className="text-[11px] font-medium text-[#00E5FF] bg-[#10222A] rounded-full px-2.5 py-0.5">#{t}</span>
          ))}
        </div>
      )}

      {note.has_audio && (
        <audio data-testid={`voice-note-audio-${note.id}`} controls preload="none"
               src={`${BACKEND}/api/voice-notes/${note.id}/audio`} className="w-full h-9" />
      )}

      {note.transcript && (
        <div>
          <button data-testid={`voice-note-transcript-toggle-${note.id}`} onClick={() => setOpen((v) => !v)}
                  className="text-xs font-medium text-slate-400 hover:text-[#00E5FF] inline-flex items-center gap-1">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide" : "Full transcript"}
          </button>
          {open && <p className="mt-2 text-sm text-slate-400 whitespace-pre-wrap leading-relaxed bg-[#07090E] rounded-xl p-3 border border-[#1E273D]">{note.transcript}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1 border-t border-[#1E273D]">
        <button data-testid={`voice-note-explore-${note.id}`} onClick={() => onAction(note, "explore")}
                className="h-9 px-3.5 rounded-full border border-[#29344D] text-[#00E5FF] hover:border-[#00E5FF]/50 hover:bg-[#10222A] text-xs font-semibold flex items-center gap-1.5 transition-colors">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.7} /> Explore as a study topic
        </button>
        <button data-testid={`voice-note-study-${note.id}`} disabled={busy === `study-${note.id}`}
                onClick={() => onAction(note, "study")}
                className="h-9 px-3.5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors">
          {busy === `study-${note.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookOpen className="w-3.5 h-3.5" strokeWidth={1.7} />}
          Generate a full study <Lock className="w-3 h-3 opacity-70" />
        </button>
      </div>
    </div>
  );
}

export default function VoiceNotes() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState("");
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const unused = notes.filter((n) => !n.used);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/voice-notes");
      setNotes(data.notes || []);
    } catch (e) {
      toast.error("Couldn't load your voice notes");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const pickMime = () => {
    const prefs = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    for (const m of prefs) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
    }
    return "";
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Your browser doesn't support audio recording.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => upload(new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" }));
      mr.start();
      mediaRef.current = mr;
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access was blocked. Enable it in your browser settings to record.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    try { mediaRef.current?.stop(); } catch (e) { /* ignore */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const upload = async (blob) => {
    if (!blob || blob.size < 1000) {
      toast.error("That recording was too short — try again.");
      return;
    }
    setUploading(true);
    const ext = (blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm");
    const fd = new FormData();
    fd.append("file", blob, `voice-note.${ext}`);
    try {
      const { data } = await api.post("/voice-notes", fd);
      setNotes((n) => [data, ...n]);
      toast.success("Transcribed & organised");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save that note");
    }
    setUploading(false);
  };

  const del = async (note) => {
    if (!window.confirm("Delete this voice note?")) return;
    const prev = notes;
    setNotes((n) => n.filter((x) => x.id !== note.id));
    try { await api.delete(`/voice-notes/${note.id}`); toast.success("Deleted"); }
    catch (e) { setNotes(prev); toast.error("Couldn't delete"); }
  };

  const doAction = async (note, kind) => {
    if (kind === "explore") {
      const seed = note.study_seed || note.title || "";
      try {
        localStorage.setItem("owp_intended_study", JSON.stringify({
          mode: /\d/.test(seed) ? "passage" : "topic", input: seed,
        }));
      } catch (e) { /* ignore */ }
      navigate("/study");
      return;
    }
    if (kind === "study") {
      setBusy(`study-${note.id}`);
      try {
        const { data } = await api.post(`/voice-notes/${note.id}/to-study`);
        toast.success("Building your study…");
        navigate(`/study/${data.id}`);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the study");
      }
      setBusy("");
    }
  };

  return (
    <div data-testid="voice-notes-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
      <header>
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-1">OpenWord AI</div>
        <h1 className="text-3xl sm:text-4xl text-white">Voice Notes</h1>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed max-w-lg">
          Caught a thought with no time to write it down? Tap record, speak freely, and we&rsquo;ll transcribe
          and tidy it into a note — then turn it into a study whenever you&rsquo;re ready.
        </p>
      </header>

      {!loading && !nudgeDismissed && unused.length > 0 && (
        <div data-testid="voice-notes-nudge" className="flex items-start gap-3 rounded-2xl border border-[#00E5FF]/30 bg-[#10222A] px-4 py-3">
          <Sparkles className="w-4 h-4 text-[#00E5FF] mt-0.5 shrink-0" strokeWidth={1.7} />
          <div className="flex-1 text-sm text-[#00B8CC]">
            You have <strong>{unused.length}</strong> thought{unused.length > 1 ? "s" : ""} you haven&rsquo;t turned into a study yet.
            <button data-testid="voice-nudge-explore" onClick={() => doAction(unused[unused.length - 1], "explore")}
                    className="ml-1 underline font-medium hover:text-[#00E5FF]">Explore the oldest &rarr;</button>
          </div>
          <button data-testid="voice-nudge-dismiss" onClick={() => setNudgeDismissed(true)}
                  className="text-[#00E5FF]/60 hover:text-[#00E5FF]" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Recorder */}
      <div data-testid="voice-recorder" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 flex flex-col items-center gap-4">
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2 text-[#00E5FF]" data-testid="voice-uploading">
            <Loader2 className="w-8 h-8 animate-spin" strokeWidth={1.5} />
            <span className="text-sm text-slate-400">Transcribing &amp; organising your thought…</span>
          </div>
        ) : (
          <>
            <button
              data-testid={recording ? "voice-stop-btn" : "voice-record-btn"}
              onClick={recording ? stopRecording : startRecording}
              className={`w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-sm ${
                recording ? "bg-red-500/100 hover:bg-red-600 animate-pulse" : "bg-[#00E5FF] hover:bg-[#00B8CC]"
              }`}
              aria-label={recording ? "Stop recording" : "Start recording"}
            >
              {recording ? <Square className="w-7 h-7 text-white" fill="white" /> : <Mic className="w-8 h-8 text-white" strokeWidth={1.6} />}
            </button>
            <div className="text-center">
              {recording ? (
                <span data-testid="voice-timer" className="text-sm font-medium text-red-500 tabular-nums">Recording · {fmtClock(elapsed)}</span>
              ) : (
                <span className="text-sm text-slate-400">Tap to record a thought</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Notes list */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-400">
          <Wand2 className="w-3.5 h-3.5 text-[#00E5FF]" strokeWidth={1.6} /> Your thoughts
        </div>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>
        ) : notes.length === 0 ? (
          <div data-testid="voice-notes-empty" className="rounded-2xl border border-dashed border-[#29344D] py-12 text-center text-sm text-slate-400">
            No voice notes yet. Record your first thought above.
          </div>
        ) : (
          notes.map((n) => <NoteCard key={n.id} note={n} onDelete={del} onAction={doAction} busy={busy} />)
        )}
      </section>
    </div>
  );
}
